import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DirhamSymbol } from 'dirham/react';
import { FaInstagram } from 'react-icons/fa';
import 'dirham/css';

const TiltCard = ({ children, className, style }) => {
  const [tiltStyle, setTiltStyle] = useState({});
  
  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - left) / width;
    const y = (e.clientY - top) / height;
    const rotateX = (0.5 - y) * 15; // Max 15 degrees tilt
    const rotateY = (x - 0.5) * 15;
    setTiltStyle({
      transform: `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`,
      transition: 'transform 0.1s ease-out'
    });
  };
  
  const handleMouseLeave = () => {
    setTiltStyle({
      transform: 'perspective(1200px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
      transition: 'transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)'
    });
  };

  return (
    <div 
      onMouseMove={handleMouseMove} 
      onMouseLeave={handleMouseLeave} 
      style={{ ...style, ...tiltStyle, transformStyle: 'preserve-3d' }}
      className={className}
    >
      {children}
    </div>
  );
};

const ScrollReveal = ({ children, className = '', delay = 0, style = {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div 
      ref={ref} 
      className={`${className} reveal-hidden ${isVisible ? 'reveal-visible' : ''}`} 
      style={{ ...style, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const TizzaLandingPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Trigger initial load animations
    setTimeout(() => setIsLoaded(true), 100);

    const handleScroll = () => {
      setScrollY(window.scrollY);
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    // Use requestAnimationFrame for smoother scroll updates
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);
    
    // Add fonts if not already present
    if (!document.getElementById('tizza-fonts')) {
      const link = document.createElement('link');
      link.id = 'tizza-fonts';
      link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@300;400;500;600&family=Outfit:wght@300;400;700;800&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const themeColors = {
    green: '#2f3f22',
    beige: '#e3cca3',
    beigeDark: '#cbb287'
  };


  const menuItems = [
    {
      id: 1,
      name: 'Trio Fruits',
      description: 'Luscious layers of fresh mango, ripe strawberry coulis, and creamy coconut flakes in a signature artisanal box.',
      image: '/images/tizza/fruitrio.webp',
      price: <><DirhamSymbol /> 22</>,
      badge: 'Special'
    },
    {
      id: 2,
      name: 'Mustard Paneer Croissant',
      description: 'Flaky artisanal butter croissant stuffed with spiced paneer, melted cheese, caramelized onions, and tangy mustard drizzle.',
      image: '/images/tizza/1786791319538_Mustard_Paneer_Croissant.jpg',
      price: <><DirhamSymbol /> 18</>,
      badge: 'New'
    },
    {
      id: 3,
      name: 'Nashville Chick Fries',
      description: 'Crispy golden fries loaded with spicy Nashville chicken bites, creamy ranch drizzle, melted cheese, and crunchy tortilla crisps.',
      image: '/images/tizza/1786706284680_Nashville_Chick_Fries.webp',
      price: <><DirhamSymbol /> 20</>,
      badge: 'Popular'
    },
    {
      id: 4,
      name: 'Mocktail Mango Splash',
      description: 'Refreshing tropical blend of sweet mango nectar, zesty lime, blue curaçao syrup, fresh mint, and sparkling soda.',
      image: '/images/tizza/1786706382051_Mango_Splash.webp',
      price: <><DirhamSymbol /> 16</>,
      badge: 'Refreshing'
    },
    {
      id: 5,
      name: 'Classic Ice Tea',
      description: 'Slow-steeped artisan black tea infused with fresh citrus lemon, aromatic mint, and velvety cold foam topping.',
      image: '/images/tizza/1786791019619_Classic_Ice_tea.webp',
      price: <><DirhamSymbol /> 16</>,
      badge: 'Chilled'
    },
    {
      id: 6,
      name: 'Spicy Green Chick Pop',
      description: 'Crispy chicken pops tossed in vibrant green herb aioli, black olives, chili flakes, and served with house dipping sauces.',
      image: '/images/tizza/1786694935118_Spicy_Green_Chick_Pop.webp',
      price: <><DirhamSymbol /> 20</>,
      badge: 'Spicy'
    }
  ];

  const reviews = [
    {
      id: 1,
      name: 'Aisha Al-Nuaimi',
      role: 'Local Guide • Dubai',
      avatar: 'AN',
      rating: 5,
      source: 'Google Review',
      date: 'Recent Visit',
      favoriteDish: 'Trio Fruits & Lotus Biscoff',
      comment: 'The Trio Fruits dessert box was an absolute revelation! Fresh, delicate sweetness and the packaging is so luxurious. The late-night hours in Al Quoz make it our favorite hangout.'
    },
    {
      id: 2,
      name: 'Farhan Tariq',
      role: 'Food Connoisseur',
      avatar: 'FT',
      rating: 5,
      source: 'Verified Foodie',
      date: 'Dine-in Guest',
      favoriteDish: 'Nashville Chick Fries & Mango Splash',
      comment: 'Hands down the best loaded fries I’ve had in Dubai! The Nashville Chick Fries have the perfect crunch and fiery kick, perfectly balanced with the cold Mango Splash mocktail.'
    },
    {
      id: 3,
      name: 'Sneha Patel',
      role: 'Verified Foodie',
      avatar: 'SP',
      rating: 5,
      source: 'Google Review',
      date: 'Late Night Diner',
      favoriteDish: 'Mustard Paneer Croissant',
      comment: 'The Mustard Paneer Croissant was flaky perfection with an incredible fusion flavour. Love that they are open till 3 AM — it has become our go-to midnight craving spot!'
    },
    {
      id: 4,
      name: 'Rashid Al-Maktoum',
      role: 'Regular Guest',
      avatar: 'RM',
      rating: 5,
      source: 'Google Review',
      date: 'Weekend Regular',
      favoriteDish: 'Spicy Green Chick Pop',
      comment: 'Truly a unique fusion food concept. Every dish feels thoughtfully crafted by culinary masters. Ismail MK & Chef Nijju have built something exceptional here.'
    }
  ];

  const [currentReview, setCurrentReview] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [touchStartX, setTouchStartX] = useState(null);

  useEffect(() => {
    if (!isAutoPlay) return;
    const timer = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % reviews.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [isAutoPlay, reviews.length]);

  const handlePrevReview = () => {
    setIsAutoPlay(false);
    setCurrentReview((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  };

  const handleNextReview = () => {
    setIsAutoPlay(false);
    setCurrentReview((prev) => (prev + 1) % reviews.length);
  };

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (diff > 50) {
      handleNextReview();
    } else if (diff < -50) {
      handlePrevReview();
    }
    setTouchStartX(null);
  };


  return (
    <div style={{ backgroundColor: themeColors.green, color: themeColors.beige, minHeight: '100vh', fontFamily: "'Inter', sans-serif", overflowX: 'hidden' }}>
      
      {/* Custom Styles for Animations & Professional Design */}
      <style>
        {`
          @keyframes fadeInUp3D {
            from { 
              opacity: 0; 
              transform: translateY(40px) rotateX(-15deg) scale(0.95); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0) rotateX(0deg) scale(1); 
            }
          }
          .animate-fade-in-up {
            opacity: 0;
            transform-origin: center bottom;
            animation: fadeInUp3D 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
          }
          .delay-100 { animation-delay: 0.1s; }
          .delay-200 { animation-delay: 0.2s; }
          .delay-300 { animation-delay: 0.3s; }
          .delay-400 { animation-delay: 0.4s; }
          .delay-500 { animation-delay: 0.5s; }
          
          .reveal-hidden {
            opacity: 0;
            transform: translateY(40px) rotateX(-10deg);
            transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
            transform-origin: center bottom;
          }
          .reveal-visible {
            opacity: 1;
            transform: translateY(0) rotateX(0deg);
          }
          
          .glass-panel {
            background: rgba(227, 204, 163, 0.02);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border: 1px solid rgba(227, 204, 163, 0.08);
            transform-style: preserve-3d;
          }
          
          .hover-glow {
            transition: all 0.4s ease;
          }
          .hover-glow:hover {
            box-shadow: 0 0 30px rgba(227, 204, 163, 0.3);
            transform: translateY(-2px);
          }
          
          .btn-outline {
            position: relative;
            overflow: hidden;
            transition: all 0.4s ease;
          }
          .btn-outline::before {
            content: '';
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background: ${themeColors.beige};
            transform: scaleX(0);
            transform-origin: right;
            transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
            z-index: -1;
          }
          .btn-outline:hover::before {
            transform: scaleX(1);
            transform-origin: left;
          }
          .btn-outline:hover {
            color: ${themeColors.green} !important;
          }

        `}
      </style>

      {/* Navbar */}
      <nav 
        className="fixed w-full z-50 transition-all duration-500 ease-in-out"
        style={{ 
          padding: isScrolled ? '1rem 2rem' : '2.5rem 2rem',
          backgroundColor: isScrolled ? 'rgba(47, 63, 34, 0.95)' : 'transparent',
          backdropFilter: isScrolled ? 'blur(15px)' : 'none',
          borderBottom: isScrolled ? '1px solid rgba(227, 204, 163, 0.1)' : '1px solid transparent'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className={`flex flex-col items-center cursor-pointer transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} onClick={() => window.scrollTo(0, 0)}>
            <img src="/images/tizza/tizza-logo.png" alt="Tizza & Co. Logo" className="h-16 w-auto object-contain drop-shadow-md" />
          </div>
          
          <div className={`hidden md:flex space-x-10 text-xs uppercase tracking-[0.2em] font-medium transition-opacity duration-700 delay-100 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <a href="#about" className="relative hover:opacity-100 opacity-70 transition-opacity after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[#e3cca3] hover:after:w-full after:transition-all after:duration-300">About Us</a>
            <a href="#menu" className="relative hover:opacity-100 opacity-70 transition-opacity after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[#e3cca3] hover:after:w-full after:transition-all after:duration-300">Menu</a>
            <a href="#reviews" className="relative hover:opacity-100 opacity-70 transition-opacity after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[#e3cca3] hover:after:w-full after:transition-all after:duration-300">Reviews</a>
            <a href="#location" className="relative hover:opacity-100 opacity-70 transition-opacity after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[#e3cca3] hover:after:w-full after:transition-all after:duration-300">Location</a>
          </div>
          
          <div className={`transition-opacity duration-700 delay-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <button 
              onClick={() => navigate('/tizza')}
              className="px-7 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] font-semibold hover-glow"
              style={{ backgroundColor: themeColors.beige, color: themeColors.green }}
            >
              Order Now
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden perspective" style={{ perspective: '1000px' }}>
        {/* Background Image with Parallax */}
        <div 
          className="absolute inset-0 z-0 ease-out"
          style={{
            backgroundImage: "url('/images/tizza/coverimage.webp')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateZ(-100px) scale(1.1) translateY(${scrollY * 0.4}px)`,
          }}
        >
          {/* Moody Overlay with Vignette */}
          <div className="absolute inset-0" style={{ 
            background: 'radial-gradient(circle at center, rgba(47, 63, 34, 0.4) 0%, rgba(47, 63, 34, 0.85) 100%)' 
          }}></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#2f3f22] via-transparent to-transparent opacity-90"></div>
        </div>

        {/* Hero Content with Inverse Parallax */}
        <div 
          className="relative z-10 text-center px-4 max-w-5xl mx-auto flex flex-col items-center mt-20" 
          style={{ 
            transformStyle: 'preserve-3d',
            transform: `translateY(${scrollY * 0.15}px)`
          }}
        >
          {isLoaded && (
            <>
              <div className="flex items-center space-x-4 mb-8 animate-fade-in-up" style={{ transform: 'translateZ(20px)' }}>
                <div className="h-[1px] w-12 bg-[#e3cca3] opacity-60"></div>
                <p className="uppercase tracking-[0.4em] text-xs md:text-sm font-medium opacity-90">Welcome to Tizza & Co.</p>
                <div className="h-[1px] w-12 bg-[#e3cca3] opacity-60"></div>
              </div>
              
              <h2 
                className="text-6xl md:text-8xl lg:text-9xl font-bold mb-8 uppercase animate-fade-in-up delay-100" 
                style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em', lineHeight: '0.95', transform: 'translateZ(50px)' }}
              >
                Crafted for <br/>
                <span className="italic font-light lowercase" style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '0' }}>craving.</span>
              </h2>
              
              <p className="text-base md:text-xl mb-12 max-w-2xl mx-auto opacity-80 font-light leading-relaxed animate-fade-in-up delay-200" style={{ transform: 'translateZ(30px)' }}>
                A new era of Fusion Food Culture — bringing together global flavours, cultures, and culinary creativity.
              </p>
              
              <div className="flex flex-col sm:flex-row space-y-5 sm:space-y-0 sm:space-x-6 animate-fade-in-up delay-300" style={{ transform: 'translateZ(40px)' }}>
                <button 
                  onClick={() => navigate('/tizza')}
                  className="px-10 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold hover-glow"
                  style={{ backgroundColor: themeColors.beige, color: themeColors.green }}
                >
                  Explore Menu
                </button>
                <a 
                  href="#about"
                  className="btn-outline px-10 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold border border-[#e3cca3] z-10 inline-block text-center"
                  style={{ color: themeColors.beige }}
                >
                  Our Vision
                </a>
              </div>
            </>
          )}
        </div>
        
        {/* Scroll Indicator */}
        <div className={`absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 transition-opacity duration-1000 delay-500 ${isLoaded ? 'opacity-70' : 'opacity-0'} animate-bounce`}>
          <a href="#about" aria-label="Scroll to About section">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={themeColors.beige} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M19 12l-7 7-7-7"/>
            </svg>
          </a>
        </div>
      </section>

      {/* About Us / Brand Vision Section */}
      <section id="about" className="py-14 md:py-24 px-4 sm:px-6 relative overflow-hidden">
        {/* Background Ambient Glows */}
        <div className="absolute top-1/4 -left-40 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none" style={{ background: themeColors.beige }}></div>
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none" style={{ background: themeColors.beige }}></div>

        <div className="max-w-6xl mx-auto">
          
          {/* Main Hero Story Grid */}
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-20">
            {/* Left Image Showcase with 3D Tilt */}
            <ScrollReveal className="lg:w-1/2 w-full relative group" delay={0}>
              <TiltCard className="w-full relative" style={{ perspective: '1000px' }}>
                {/* Decorative border frame */}
                <div 
                  className="absolute -inset-4 border border-[#e3cca3] opacity-20 transform -rotate-3 transition-transform duration-700 group-hover:rotate-0 rounded-2xl" 
                  style={{ transform: `translateZ(-20px) rotate(-3deg) translateY(${(scrollY - 600) * 0.08}px)` }}
                ></div>
                
                {/* Image container */}
                <div className="relative p-2.5 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundColor: 'rgba(227, 204, 163, 0.15)', backdropFilter: 'blur(8px)', transform: 'translateZ(10px)' }}>
                  <video 
                    src="/images/tizza/VIDEO-2026-09-21-13-53-41.mp4" 
                    poster="/images/tizza/canndessert/1786792203813_Tender_White.jpg"
                    autoPlay 
                    loop 
                    muted 
                    playsInline 
                    className="w-full h-[520px] object-cover rounded-xl filter contrast-110 transition-all duration-700 group-hover:scale-105" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1b2514]/95 via-[#1b2514]/40 to-transparent pointer-events-none"></div>

                  {/* Floating Instagram Button on Video */}
                  <a
                    href="https://www.instagram.com/tizza.ae?igsi=dDNmemt0ZnIzOTB4"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-5 left-5 z-20 flex items-center space-x-2 px-3.5 py-2 rounded-full border border-[#e3cca3]/40 shadow-2xl transition-all duration-300 hover:scale-105 hover:bg-[#e3cca3] group/ig text-[#e3cca3] hover:text-[#2f3f22]"
                    style={{
                      backgroundColor: 'rgba(27, 37, 20, 0.88)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      transform: 'translateZ(35px)'
                    }}
                    aria-label="Follow Tizza & Co. on Instagram"
                  >
                    <FaInstagram className="text-base transition-transform duration-300 group-hover/ig:rotate-6 text-[#e3cca3] group-hover/ig:text-[#2f3f22]" />
                    <span className="text-xs font-semibold tracking-wider font-sans">@tizza.ae</span>
                  </a>
                  
                  {/* Floating Chairman Badge inside image */}
                  <div 
                    className="absolute bottom-6 left-6 right-6 p-5 rounded-xl border border-[#e3cca3]/30 shadow-2xl transition-all duration-300" 
                    style={{ 
                      backgroundColor: 'rgba(27, 37, 20, 0.88)', 
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      transform: 'translateZ(30px)' 
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#e3cca3] font-bold mb-1 opacity-90">Visionary Leadership</p>
                        <h4 className="text-xl font-bold text-[#e3cca3]" style={{ fontFamily: "'Outfit', sans-serif" }}>Ismail MK</h4>
                        <p className="text-xs text-[#e3cca3]/90 font-medium">Chairman &amp; Founder</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-[#e3cca3]/15 border border-[#e3cca3]/50 flex items-center justify-center text-[#e3cca3] text-xs font-serif font-bold italic shadow-inner">
                        MK
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Overlay Badge */}
                <div 
                  className="absolute -top-6 -right-6 p-5 rounded-2xl flex flex-col items-center justify-center shadow-2xl z-20 border border-[#e3cca3]/30"
                  style={{ 
                    backgroundColor: 'rgba(27, 37, 20, 0.92)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    transform: `translateZ(60px) translateY(${(scrollY - 600) * -0.04}px)` 
                  }}
                >
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#e3cca3] text-center">
                    Fusion Food<br/>Express
                  </span>
                </div>
              </TiltCard>
            </ScrollReveal>
            
            {/* Right Text Content */}
            <ScrollReveal className="lg:w-1/2 space-y-8" delay={200}>
              <div className="flex items-center space-x-4 mb-2">
                <div className="h-[1px] w-8 bg-[#e3cca3] opacity-60"></div>
                <h3 className="text-xs uppercase tracking-[0.3em] font-semibold opacity-80" style={{ color: themeColors.beige }}>About Us</h3>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-tight tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                What is <br/>
                <span className="text-[#e3cca3]">Tizza &amp; Co.?</span>
              </h2>
              
              <p className="text-xl md:text-2xl font-light text-[#e3cca3] leading-relaxed border-l-2 border-[#e3cca3]/50 pl-5 italic">
                “TIZZA &amp; CO is more than just a food brand. It is a vision to create a new experience through food.”
              </p>

              <div className="space-y-4 text-base md:text-lg font-light opacity-85 leading-relaxed">
                <p>
                  Our goal is to bring together different flavours, cultures and ideas from around the world and create a new <strong className="text-[#e3cca3] font-semibold">Fusion Food Culture</strong>.
                </p>
                <p>
                  This unique vision was created by our <strong className="text-[#e3cca3] font-semibold">Chairman &amp; Founder, Ismail MK</strong>. His vision was to look at the cafeteria industry differently and create a concept that goes beyond the traditional cafeteria model.
                </p>
              </div>

              {/* Highlighted Quote Box */}
              <div className="glass-panel p-6 rounded-2xl relative overflow-hidden border border-[#e3cca3]/20 hover-glow">
                <div className="text-5xl font-serif text-[#e3cca3]/20 absolute -top-2 left-3 select-none leading-none">“</div>
                <p className="text-lg md:text-xl font-medium text-center text-[#e3cca3] italic tracking-wide relative z-10 pt-2">
                  "Food should not just be eaten.<br/>It should be experienced."
                </p>
                <div className="h-0.5 w-16 bg-[#e3cca3]/40 mx-auto mt-3"></div>
              </div>

              {/* Instagram Action Button */}
              <div className="pt-1 flex flex-wrap items-center gap-4">
                <a
                  href="https://www.instagram.com/tizza.ae?igsi=dDNmemt0ZnIzOTB4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-3 px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.2em] font-semibold border border-[#e3cca3] hover:bg-[#e3cca3] hover:text-[#2f3f22] transition-all duration-300 shadow-xl hover-glow group/igbtn"
                  style={{ color: themeColors.beige }}
                >
                  <FaInstagram className="text-base group-hover/igbtn:scale-125 transition-transform duration-300 text-[#e3cca3] group-hover/igbtn:text-[#2f3f22]" />
                  <span>Follow on Instagram</span>
                  <svg 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="transform group-hover/igbtn:translate-x-1 transition-transform"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Menu Highlights Section */}
      <section id="menu" className="py-14 md:py-24 px-4 sm:px-6 relative border-y border-[#e3cca3]/10 overflow-hidden" style={{ backgroundColor: 'rgba(227, 204, 163, 0.02)' }}>
        
        {/* Parallax Background Text Watermark */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 flex items-center justify-center">
          <div 
            className="absolute font-bold opacity-[0.015] whitespace-nowrap text-[#e3cca3]" 
            style={{ 
              fontSize: '25rem', 
              fontFamily: "'Bebas Neue', sans-serif", 
              transform: `translateX(${(scrollY - 1200) * -0.3}px)` 
            }}
          >
            TIZZA & CO. FUSION
          </div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal className="text-center mb-10 md:mb-16" delay={0}>
            <div className="flex justify-center items-center space-x-4 mb-4">
              <div className="h-[1px] w-12 bg-[#e3cca3] opacity-40"></div>
              <h3 className="text-xs uppercase tracking-[0.4em] font-semibold opacity-80" style={{ color: themeColors.beige }}>Craving Satisfiers</h3>
              <div className="h-[1px] w-12 bg-[#e3cca3] opacity-40"></div>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>Signature Flavours</h2>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {menuItems.map((item, index) => (
              <div 
                key={item.id} 
                className="group relative w-full rounded-3xl overflow-hidden cursor-pointer shadow-2xl transition-all duration-500 hover:shadow-xl" 
                style={{ 
                  height: '400px',
                  backgroundColor: '#2f3f22',
                  marginTop: (index % 2 === 1) ? '24px' : '0px' 
                }}
                onClick={() => navigate('/tizza')}
              >
                  {/* Premium Badge */}
                  {item.badge && (
                    <div 
                      className="absolute top-4 right-4 z-20 text-xs uppercase font-bold px-3 py-1 rounded-full shadow-lg"
                      style={{ backgroundColor: '#e3cca3', color: '#2f3f22', letterSpacing: '0.1em' }}
                    >
                      {item.badge}
                    </div>
                  )}

                  {/* Image Section */}
                  <div className="absolute top-0 left-0 w-full overflow-hidden" style={{ height: '60%' }}>
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className="w-full h-full object-cover transition-transform duration-1000 ease-out transform group-hover:scale-110"
                    />
                    <div className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-0" style={{ backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.2), transparent, rgba(47,63,34,0.5))' }}></div>
                  </div>
                  
                  {/* Solid/Frosted Text Section */}
                  <div className="absolute bottom-0 left-0 w-full flex flex-col justify-between rounded-t-2xl p-6 transition-all duration-500 z-10" style={{ height: '45%', backgroundColor: 'rgba(43, 56, 32, 0.98)', borderTop: '1px solid rgba(227, 204, 163, 0.3)' }}>
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-2xl font-bold uppercase pr-2 leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.name}</h4>
                        <span className="text-xl font-light italic" style={{ color: '#e3cca3' }}>{item.price}</span>
                      </div>
                      <div className="h-px w-12 mb-4 opacity-70" style={{ backgroundImage: 'linear-gradient(to right, #e3cca3, transparent)' }}></div>
                      <p className="font-light text-sm opacity-80 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {item.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center text-xs uppercase font-semibold opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0" style={{ color: '#e3cca3', letterSpacing: '0.2em' }}>
                      Add to Order
                      <svg className="w-4 h-4 ml-2 transition-transform duration-300 transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                    </div>
                  </div>
              </div>
            ))}
          </div>

          <ScrollReveal className="text-center mt-12" delay={200}>
            <button 
              onClick={() => navigate('/tizza')}
              className="btn-outline px-12 py-4 text-sm uppercase tracking-[0.2em] font-semibold border border-[#e3cca3] inline-flex items-center group z-10"
              style={{ color: themeColors.beige }}
            >
              Order Online Now 
              <svg className="w-4 h-4 ml-3 transform transition-transform group-hover:translate-x-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </button>
          </ScrollReveal>
        </div>
      </section>

      {/* Reviews / Testimonials Carousel Section */}
      <section id="reviews" className="py-16 md:py-20 px-4 sm:px-6 relative overflow-hidden">
        {/* Subtle Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] rounded-full opacity-10 blur-3xl pointer-events-none" style={{ background: themeColors.beige }}></div>

        <div className="max-w-4xl mx-auto relative z-10">
          {/* Compact Header */}
          <ScrollReveal className="text-center mb-8" delay={0}>
            <div className="flex justify-center items-center space-x-3 mb-2">
              <div className="h-[1px] w-8 bg-[#e3cca3] opacity-40"></div>
              <h3 className="text-[11px] uppercase tracking-[0.35em] font-semibold opacity-80" style={{ color: themeColors.beige }}>Guest Experiences</h3>
              <div className="h-[1px] w-8 bg-[#e3cca3] opacity-40"></div>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold uppercase mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Loved By Foodies
            </h2>
            
            {/* Integrated Compact Rating Badge */}
            <div className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 px-4 py-1.5 rounded-full bg-[#e3cca3]/10 border border-[#e3cca3]/20 text-xs text-[#e3cca3]">
              <span className="font-semibold flex items-center">
                ★ 4.9 Rating
              </span>
              <span className="opacity-30">•</span>
              <span>350+ Google Reviews</span>
              <span className="opacity-30 hidden sm:inline">•</span>
              <span className="hidden sm:inline">100% Artisanal</span>
              <span className="opacity-30 hidden sm:inline">•</span>
              <span className="hidden sm:inline">Till 3 AM</span>
            </div>
          </ScrollReveal>

          {/* Carousel Showcase */}
          <ScrollReveal delay={100} className="relative">
            {/* Navigation Buttons (Desktop) */}
            <button
              onClick={handlePrevReview}
              aria-label="Previous review"
              className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-[#e3cca3]/30 bg-[#1b2514]/90 text-[#e3cca3] hover:bg-[#e3cca3] hover:text-[#2f3f22] items-center justify-center transition-all duration-300 z-20 shadow-xl"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
            </button>
            <button
              onClick={handleNextReview}
              aria-label="Next review"
              className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-[#e3cca3]/30 bg-[#1b2514]/90 text-[#e3cca3] hover:bg-[#e3cca3] hover:text-[#2f3f22] items-center justify-center transition-all duration-300 z-20 shadow-xl"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"></path></svg>
            </button>

            {/* Slider Container */}
            <div 
              className="overflow-hidden rounded-3xl"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onMouseEnter={() => setIsAutoPlay(false)}
              onMouseLeave={() => setIsAutoPlay(true)}
            >
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentReview * 100}%)` }}
              >
                {reviews.map((review) => (
                  <div key={review.id} className="w-full flex-shrink-0 px-1">
                    <div 
                      className="glass-panel rounded-3xl p-6 sm:p-8 md:p-10 border border-[#e3cca3]/20 hover:border-[#e3cca3]/40 transition-all duration-500 shadow-2xl relative"
                      style={{ backgroundColor: 'rgba(27, 37, 20, 0.85)', backdropFilter: 'blur(16px)' }}
                    >
                      {/* Top row: Avatar, Info, Stars & Source */}
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-[#e3cca3]/15">
                        <div className="flex items-center space-x-3.5">
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-xs bg-[#e3cca3]/15 border border-[#e3cca3]/40 text-[#e3cca3] shadow-inner font-serif">
                            {review.avatar}
                          </div>
                          <div>
                            <h4 className="text-base md:text-lg font-bold text-[#e3cca3] leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                              {review.name}
                            </h4>
                            <p className="text-xs opacity-70 font-light">{review.role}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          {/* 5 Stars */}
                          <div className="flex items-center space-x-1">
                            {[...Array(review.rating)].map((_, i) => (
                              <svg key={i} className="w-4 h-4 text-[#e3cca3] fill-current drop-shadow-[0_0_6px_rgba(227,204,163,0.5)]" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <span className="text-[10px] uppercase font-semibold tracking-wider px-2.5 py-1 rounded-full bg-[#e3cca3]/10 text-[#e3cca3] border border-[#e3cca3]/20">
                            {review.source}
                          </span>
                        </div>
                      </div>

                      {/* Comment */}
                      <p className="text-base sm:text-lg md:text-xl font-light leading-relaxed opacity-90 italic mb-5">
                        “{review.comment}”
                      </p>

                      {/* Bottom row: Dish & Date */}
                      <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
                        <div className="inline-flex items-center text-xs text-[#e3cca3] bg-[#e3cca3]/10 px-3 py-1 rounded-full border border-[#e3cca3]/20">
                          <span className="font-semibold mr-1.5">★ Loved:</span>
                          <span className="font-light">{review.favoriteDish}</span>
                        </div>
                        <span className="text-[11px] opacity-50 font-light">{review.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Controls: Prev/Next on Mobile + Indicator Dots */}
            <div className="flex items-center justify-between sm:justify-center gap-4 mt-5">
              <button
                onClick={handlePrevReview}
                aria-label="Previous review"
                className="flex md:hidden w-8 h-8 rounded-full border border-[#e3cca3]/30 bg-[#1b2514]/90 text-[#e3cca3] items-center justify-center"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
              </button>

              <div className="flex items-center space-x-2">
                {reviews.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setIsAutoPlay(false);
                      setCurrentReview(idx);
                    }}
                    aria-label={`Go to review ${idx + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      currentReview === idx 
                        ? 'w-7 bg-[#e3cca3]' 
                        : 'w-2 bg-[#e3cca3]/30 hover:bg-[#e3cca3]/60'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={handleNextReview}
                aria-label="Next review"
                className="flex md:hidden w-8 h-8 rounded-full border border-[#e3cca3]/30 bg-[#1b2514]/90 text-[#e3cca3] items-center justify-center"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"></path></svg>
              </button>
            </div>

            {/* Compact Action Buttons */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <button 
                onClick={() => navigate('/tizza')}
                className="px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] font-semibold hover-glow"
                style={{ backgroundColor: themeColors.beige, color: themeColors.green }}
              >
                Order Now
              </button>
              <a 
                href="https://g.page/r/CZpd5aSmGV7aEBM/review"
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-outline px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] font-semibold border border-[#e3cca3] inline-flex items-center group z-10"
                style={{ color: themeColors.beige }}
              >
                Share Your Feedback
                <svg className="w-3.5 h-3.5 ml-1.5 transform transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer id="location" className="pt-16 pb-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
          <ScrollReveal className="md:col-span-2 pr-10" delay={0}>
            <div className="flex flex-col items-start mb-8">
              <img src="/images/tizza/tizza-logo.png" alt="Tizza & Co. Logo" className="h-24 w-auto object-contain" />
            </div>
            <p className="opacity-60 max-w-md font-light leading-relaxed text-sm mb-4">
              TIZZA &amp; CO. — Fusion Food Express. Bringing together flavours, cultures, and culinary creativity under the vision of Chairman &amp; Founder Ismail MK, in collaboration with Chef Nijju International Consulting Pvt Ltd.
            </p>
            <p className="text-xs text-[#e3cca3]/80 italic">
              "Food should not just be eaten. It should be experienced."
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={150}>
            <h4 className="text-xs font-semibold mb-6 uppercase tracking-[0.2em] opacity-50">Visit Us</h4>
            <div className="space-y-2 opacity-80 font-light text-sm">
              <a 
                href="https://www.google.com/maps/place/Tizza%26co/@25.1512235,55.2477799,17z/data=!3m1!4b1!4m6!3m5!1s0x3e5f698267da0e55:0xda5e19a6a4e55d9a!8m2!3d25.1512187!4d55.2503548!16s%2Fg%2F11zhdg4c69?entry=ttu&g_ep=EgoyMDI2MDgxOS4wIKXMDSoASAFQAw%3D%3D" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:opacity-100 hover:text-[#e3cca3] transition-all cursor-pointer block"
              >
                Al Quoz 4, Dubai
              </a>
              <p>(near Star One Restaurant)</p>
              <p className="pt-4 hover:opacity-100 transition-opacity cursor-pointer">info@tizza.ae</p>
              <p className="hover:opacity-100 transition-opacity cursor-pointer">054 456 2040</p>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delay={300}>
            <h4 className="text-xs font-semibold mb-6 uppercase tracking-[0.2em] opacity-50">Hours</h4>
            <div className="space-y-3 opacity-80 font-light text-sm">
              <div className="flex justify-between pb-2">
                <span>Everyday</span>
                <span>12 PM - 03:00 AM</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
        
        <ScrollReveal className="flex flex-col md:flex-row justify-between items-center opacity-40 text-xs font-light pt-8 border-t border-[#e3cca3]/20" delay={400}>
          <p>&copy; {new Date().getFullYear()} Tizza & Co. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <a href="https://www.instagram.com/tizza.ae?igsi=dDNmemt0ZnIzOTB4" target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity">Instagram</a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity">Facebook</a>
            <a href="/privacy-policy" className="hover:opacity-100 transition-opacity">Privacy</a>
          </div>
        </ScrollReveal>
      </footer>
    </div>
  );
};

export default TizzaLandingPage;
